import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { cn } from "@win-reality/react-ui";
import { type ElementType, useEffect, useState } from "react";
import ReactDOM from "react-dom";

import LiveFieldStatusIcon from "../../../../assets/live-field-status-icon.svg";
import { useSocketContext } from "./SocketContext";

// Simple loader icon component
const LoaderIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const statusConfig = {
  disconnected: {
    icon: ExclamationCircleIcon as unknown as ElementType,
    iconClass: "h-5 w-5 text-red-500",
  },
  connecting: {
    icon: LoaderIcon as unknown as ElementType,
    iconClass: "h-5 w-5 text-yellow-500 animate-spin",
  },
  connected: {
    icon: null,
    iconClass: "",
  },
} as const;

const messageConfig = {
  disconnected: "Disconnected from stream",
  connecting: "Connecting to stream",
  connected: "Connected to stream",
} as const;

type StatusBadgeProps = {
  className?: string;
};

export function SocketStatus({ className }: StatusBadgeProps) {
  const { socketStatus: status } = useSocketContext();
  const { icon: Icon, iconClass } = statusConfig[status];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {status === "connected" ? (
        <img src={LiveFieldStatusIcon} alt="" className="h-5 w-auto" />
      ) : (
        Icon && <Icon className={iconClass} />
      )}
      <span className="hidden text-sm font-semibold text-gray-900 md:inline">
        {messageConfig[status]}
      </span>
    </div>
  );
}

/**
 * This component is used to render the socket status in the Magnus-Web header.
 * It uses React Portal to render the component in the DOM.
 */
export function MagnusSocketStatus({ className }: StatusBadgeProps) {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById("magnus-socket-status-container");
    setContainer(el);
  }, []);

  if (!container) return null;

  return ReactDOM.createPortal(
    <SocketStatus className={className} />,
    container,
  );
}
